import EntityMemberSchema from "_shared/entity-members/entity-member-schema";
import FeeScheduleCategorySchema from "_shared/fee-schedules/categories/fee-schedule-category-schema";
import { ProposalTeamReuseJSONZ } from "work/proposals/drafts-from-previous/proposal-team-reuse";
import WorkDocumentSchema from "work/work-agreements/contracts/_schemas/work-document-schema";
import { z } from "zod";

/** Which side of a proposal a template was saved from. Sent by name. */
export const ProposalTemplatePartyZ = z.enum(["Client", "Vendor"]);

/** One of a template's documents, in the shape a proposal's document collections already use. */
const ProposalTemplateDocumentZ = z.object({
  documentId: z.uuid(),
  isAvailable: z.boolean(),
  documentInfo: WorkDocumentSchema.WorkDocumentJSONZ.nullish(),
});

/** A team a template keeps: its people by reference, never copies of them. */
const ProposalTemplateTeamZ = z.object({
  leader: EntityMemberSchema.EntityMemberJSONZ.nullish(),
  members: z.array(EntityMemberSchema.EntityMemberJSONZ),
  marketplaceId: z.uuid().nullish(),
});

/** A saved whole-proposal template (#6129), as `GET work/proposal-templates` answers it. */
export const ProposalTemplateJSONZ = z.object({
  id: z.uuid(),
  name: z.string(),
  party: ProposalTemplatePartyZ,
  created: z.iso.datetime({ offset: true, local: true }),
  proposalName: z.string(),
  description: z.string(),
  negotiable: z.boolean(),
  teamRestricted: z.boolean(),
  conflictsCheckWaived: z.boolean(),
  team: ProposalTemplateTeamZ.nullish(),
  /** The other party's team, when the saver kept it (#6637). */
  counterpartyTeam: ProposalTemplateTeamZ.nullish(),
  feeSchedule: z.array(FeeScheduleCategorySchema.FeeScheduleCategoryJSONZ),
  discount: z.number().min(0).max(1),
  teamTemplateIds: z.array(z.uuid()),
  feeScheduleTemplateIds: z.array(z.uuid()),
  counterpartyTeamTemplateIds: z.array(z.uuid()).default([]),
  conflicts: z.array(ProposalTemplateDocumentZ),
  policies: z.array(ProposalTemplateDocumentZ),
  teamReuse: ProposalTeamReuseJSONZ.nullish(),
});

export type ProposalTemplateJSON = z.infer<typeof ProposalTemplateJSONZ>;
export type ProposalTemplateTeamJSON = z.infer<typeof ProposalTemplateTeamZ>;
export type ProposalTemplateDocumentJSON = z.infer<typeof ProposalTemplateDocumentZ>;
